import React, { useState } from 'react';
import { ICampaignAdDetails } from '../../../interfaces/campaign-ads.interface';
import { Button, Dropdown, InputGroup, Spinner } from 'react-bootstrap';
import { CampaignTab } from '../../../constants/common';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../store/store';
import { getOrganizationId } from '../../../store/selector/organization.selector';
import { getActiveBrand } from '../../../store/selector/brand.selector';
import { useScheduleContentToCloudCampaignMutation } from '../../../store/api/social-media.api';
import {
    ICCSocialMedia,
    IScheduleContentToCloudCampaignRequestPayload,
    IUploadAssetsToCloudCampaignAssets,
} from '../../../interfaces/social-media.interface';
import { displayErrorToast } from '../../../utils/ToastUtils';
import PublishSchedule from '../../publish/PublishSchedule';
import PublishSocialMedia from '../../publish/PublishSocialMedia';
import { parse12HourTime } from '../../../utils/common.utils';
import { IDownloadCloudCampaignCSVRequestPayload } from '../../../pages/Apprentice/apprentice.interface';
import { getAccessToken } from '../../../store/selector/auth.selector';
import { useSelector } from 'react-redux';
import config from '../../../config';
import { Scheduler } from '../../../constants/campaign-ads';

interface IProps {
    setActiveTab: React.Dispatch<React.SetStateAction<CampaignTab>>;
    selectedCampaignAds: ICampaignAdDetails[];
    setSelectedCampaignAds: React.Dispatch<React.SetStateAction<ICampaignAdDetails[]>>;
}

const PublishTab = ({ setActiveTab, selectedCampaignAds, setSelectedCampaignAds }: IProps) => {
    const navigate = useNavigate();
    const accessToken = useSelector(getAccessToken);
    const organizationId = useAppSelector(getOrganizationId);
    const activeBrand = useAppSelector(getActiveBrand);
    const [schedultContentToCloudCampaign] = useScheduleContentToCloudCampaignMutation();

    const [days, setDays] = useState<number[]>([]);
    const [time, setTime] = useState('9:00 AM');
    const [selectedAccounts, setSelectedAccounts] = useState<ICCSocialMedia[]>([]);
    const [scheduleLoading, setScheduleLoading] = useState(false);

    const uploadToSocialMedia = async (agencyMode: boolean) => {
        if (selectedAccounts.length === 0) {
            displayErrorToast('Please select one or more account');
            return;
        }
        if (days.length === 0) {
            displayErrorToast('Please select one or more days');
            return;
        }
        if (organizationId && activeBrand) {
            setScheduleLoading(true);

            const assets: IUploadAssetsToCloudCampaignAssets[] = [];
            selectedCampaignAds.forEach((ad) => {
                assets.push({
                    campaignAdId: ad.id,
                    previewUrl: ad.preview,
                    caption: ad.caption_text,
                });
            });

            const platforms: string[] = [];
            selectedAccounts.forEach((account) => platforms.push(account.platformType));
            const { hours, minutes } = parse12HourTime(time);

            const payload: IScheduleContentToCloudCampaignRequestPayload = {
                brandId: activeBrand.id,
                organizationId: organizationId,
                agencyMode,
                assets,
                platforms: platforms,
                weeklyTimeslots: [
                    {
                        days: days,
                        hour: hours,
                        minute: minutes,
                    },
                ],
            };

            try {
                const response = await schedultContentToCloudCampaign(payload);
                setScheduleLoading(false);
                if ('data' in response) {
                    const cloudCampaignUrl = response.data;
                    if (agencyMode) {
                        const newTab = window.open(
                            cloudCampaignUrl,
                            '_blank',
                            'noopener,noreferrer',
                        );
                        if (newTab) newTab.focus();
                    } else {
                        navigate('/social', {
                            state: { url: cloudCampaignUrl },
                        });
                    }
                }
            } catch (error) {
                console.error('Upload failed:', error); // Log any errors
                displayErrorToast('Failed to upload to social media.');
                setScheduleLoading(false);
            }
        }
    };

    const handleDownloadCSV = async (scheduler: Scheduler) => {
        setScheduleLoading(true);
        try {
            const reqBody: IDownloadCloudCampaignCSVRequestPayload = {
                scheduler,
                ads: selectedCampaignAds.map((ad) => ({
                    campaignAdId: ad.id,
                    previewUrl: ad.preview,
                    caption: ad.caption_text,
                })),
            };

            if (accessToken) {
                const response = await fetch(`${config.apiBaseUrl}campaign-ads/download_csv`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(reqBody),
                });

                if (!response.ok) {
                    displayErrorToast('Failed to download CSV. Please try again');
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'bulk_upload.csv');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            setScheduleLoading(false);
        } catch (error) {
            setScheduleLoading(false);
            displayErrorToast('Failed to download CSV. Please try again');
        }
    };

    const navigateToFakeLogin = (url: string) => {
        navigate(url);
    };

    return (
        <div className="tw-w-full tw-pt-6">
            {selectedCampaignAds.length === 0 && (
                <div className="tw-w-full tw-mt-6 tw-flex tw-flex-col tw-items-center tw-gap-y-3">
                    <span>Please select some ad to publish</span>
                    <Button variant="primary" onClick={() => setActiveTab(CampaignTab.CREATE)}>
                        Select
                    </Button>
                </div>
            )}
            {selectedCampaignAds.length > 0 && (
                <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-flex-col">
                    <div className="tw-w-full tw-flex tw-justify-end">
                        <InputGroup className="custom-dropdown  tw-w-fit">
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant="primary"
                                    id="dropdown-basic"
                                    className="dropdown__toggle"
                                    disabled={scheduleLoading}
                                >
                                    {scheduleLoading ? (
                                        <>
                                            <Spinner
                                                style={{
                                                    width: '1.5rem',
                                                    height: '1.5rem',
                                                }}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            Schedule via
                                            <i className="ph ph-caret-down md"></i>
                                        </>
                                    )}
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="dropdown__toggle--menu" align="end">
                                    <Dropdown.Item onClick={() => uploadToSocialMedia(false)}>
                                        Pixii Scheduler
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => uploadToSocialMedia(true)}>
                                        Cloud Campaign
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => handleDownloadCSV(Scheduler.SMARTER_QUEUE)}
                                    >
                                        Smarter Queue CSV
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDownloadCSV(Scheduler.CC)}>
                                        Cloud Campaign CSV
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDownloadCSV(Scheduler.CC)}>
                                        Vista Social
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDownloadCSV(Scheduler.CC)}>
                                        Zoho Social
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDownloadCSV(Scheduler.CC)}>
                                        Hootsuite
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDownloadCSV(Scheduler.CC)}>
                                        Metricool
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDownloadCSV(Scheduler.CC)}>
                                        SocialPilot
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleDownloadCSV(Scheduler.CC)}>
                                        Sprout Social
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </InputGroup>
                    </div>
                    <div className="tw-w-full tw-flex tw-justify-start tw-gap-x-6 tw-mt-6">
                        <PublishSchedule
                            days={days}
                            setDays={setDays}
                            time={time}
                            setTime={setTime}
                        />
                        <PublishSocialMedia
                            selectedAccounts={selectedAccounts}
                            setSelectedAccounts={setSelectedAccounts}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublishTab;
